import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Input,
  notification,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
} from 'antd';
import Title from 'antd/es/typography/Title';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { useState } from 'react';
import { formatDate, formatNumber } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  billingEntry,
  billingEntrys,
  searchSellerBilling,
} from '@/adapters/seller';
import {
  InvoiceList,
  ISellerBilling,
  ISellerSearchBillingReq,
} from '@/interfaces/seller';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import { IServerInfoRes } from '@/interfaces/misc';
import { getServerInfo } from '@/adapters/misc';
import SellerInvoiceModal from './components/seller-invoice-modal';
import classNames from 'classnames';
import {
  getSellerBillingStatusColor,
  getSellerBillingStatusName,
  getSellerBillingStatusOptions,
} from '@/helpers/seller';
import { ESellerBillingStatus } from '@/enums/seller';
import { jobProcess } from '@/adapters/job';
import { getQueryParam } from '@/utilities/queryUtils';

const { RangePicker } = DatePicker;

const _criteria: ISellerSearchBillingReq = {
  docNo: '',
  dateFrom: dayjs().add(-1, 'month').startOf('month').format('YYYY-MM-DD'),
  dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  docStatus: ESellerBillingStatus.PENDING,
  customer: null,
};

const BillingPage: React.FC = () => {
  const job = getQueryParam('job');
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState<ISellerSearchBillingReq>({
    ..._criteria,
  });
  const [selectedRowKey, setSelectedRowKey] = useState<ISellerBilling[]>([]);
  const [invoiceModal, setInvoiceModal] = useState<{
    open: boolean;
    invoiceList: InvoiceList[];
  }>({
    open: false,
    invoiceList: [],
  });

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  const onFinish: FormProps<ISellerSearchBillingReq>['onFinish'] = (values) => {
    setCriteria({
      ...values,
      docDate: [dayjs(_criteria.dateFrom), dayjs(_criteria.dateTo)],
    });
  };

  const onClear = () => {
    form.setFieldsValue({
      ..._criteria,
    });
    setCriteria({
      ..._criteria,
    });
  };

  const columns: TableColumnsType<ISellerBilling> = [
    {
      title: 'ลำดับ',
      dataIndex: '',
      width: 40,
      align: 'center',
      className: 'text-bold',
      render: (_, rec, i) => <div className="font-weight-bold">{i + 1}</div>,
    },
    {
      title: 'เอกสารวางบิล',
      dataIndex: 'AR__CHR01',
      align: 'center',
      className: 'text-bold',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'วันที่วางบิล',
      dataIndex: 'AR_EXPT_DATE',
      align: 'center',
      className: 'text-bold',
      width: 120,
      render: (_) => formatDate(_),
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'AR_CUST',
      align: 'center',
      className: 'text-bold',
      width: 120,
    },
    {
      title: 'ชื่อลูกค้า',
      dataIndex: 'CMAD_NAME',
      className: 'text-bold',
    },
    {
      title: 'เครดิตเทอม',
      dataIndex: 'AR_CR_TERMS',
      align: 'center',
      className: 'text-bold',
      width: 120,
    },
    {
      title: 'ยอดเงิน',
      dataIndex: 'AR_AMT_TOTAL',
      align: 'right',
      width: 120,
      className: 'text-bold text-primary',
      render: (_) => formatNumber(_, 2),
    },
    {
      title: 'สถานะเอกสาร',
      dataIndex: 'AR_MSTR_BMESSSTATUS',
      align: 'center',
      width: 120,
      render: (_) => (
        <div className={classNames(getSellerBillingStatusColor(_))}>
          {getSellerBillingStatusName(_)}
        </div>
      ),
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'AR_RMKS_BILL',
      hidden: criteria.docStatus !== ESellerBillingStatus.RJCT,
    },
    {
      title: '',
      dataIndex: 'InvoiceList',
      align: 'center',
      width: 120,
      render: (_) => (
        <div>
          {
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              // style={{ minWidth: 150 }}
              onClick={() => {
                setInvoiceModal({
                  invoiceList: _,
                  open: true,
                });
              }}
            >
              invoice list
            </button>
          }
        </div>
      ),
    },
  ];

  const { data, refetch } = useQuery<AxiosResponse<any>, Error>({
    queryKey: ['search-seller-billing', criteria],
    queryFn: async () => await searchSellerBilling(criteria),
  });

  const { data: serverInfo } = useQuery<AxiosResponse<IServerInfoRes>, Error>({
    queryKey: ['search-server-info'],
    queryFn: async () => await getServerInfo(),
  });
  const billings = data?.data?.data || [];
  const rowSelection: TableProps<ISellerBilling>['rowSelection'] = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: ISellerBilling[]
    ) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        'selectedRows: ',
        selectedRows
      );
      setSelectedRowKey(selectedRows);
    },
    getCheckboxProps: (record: ISellerBilling) => ({
      disabled: !!record.AR_MSTR_BMESSID,
    }),
    selectedRowKeys: selectedRowKey.map((c) => c.AR__CHR01) || [],
  };

  const mutations = useMutation({
    mutationFn: async (data: ISellerBilling) => await billingEntry(data),
    mutationKey: ['invoice-entry'],
    onSuccess: (data) => {
      if (data.status === 200) {
        notification.success({
          message: 'ส่งเอกสารสำเร็จ',
        });
        refetch();
        setSelectedRowKey([]);
      } else {
        // const duplicateCoas: string[] = data.data?.data;
        // const updateCoa = coa.map(c => {
        //   c.status = duplicateCoas.includes(c.lotNo ?? '') ? 2 : 3;
        //   return c;
        // });
        // setCoa(updateCoa);
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  const mutationGroups = useMutation({
    mutationFn: async (data: ISellerBilling[]) => await billingEntrys(data),
    mutationKey: ['invoice-entrys'],
    onSuccess: (data) => {
      if (data.status === 200) {
        const response = data?.data;
        if (response?.data?.length) {
          response?.data?.map((c) => {
            return notification.success({
              message: c.refNo,
              description: 'ส่งอกสารสำเร็จ',
            });
          });
        } else {
          response?.errors?.map((c) => {
            return notification.error({
              message: c.refNo,
              description: c?.errors?.errorMessage,
            });
          });
        }
        refetch();
        setSelectedRowKey([]);
        refetch();
        setSelectedRowKey([]);
      } else {
        // const duplicateCoas: string[] = data.data?.data;
        // const updateCoa = coa.map(c => {
        //   c.status = duplicateCoas.includes(c.lotNo ?? '') ? 2 : 3;
        //   return c;
        // });
        // setCoa(updateCoa);
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onEntry(): void {
    mutationGroups.mutateAsync(selectedRowKey);
    // mutations.mutateAsync(selectedRowKey[0]);
  }

  return (
    <>
      <Flex justify="space-between" gap={2}>
        <div>
          <Title level={2} className="text-center">
            {serverInfo?.data?.data?.[0]?.SERVER_NAME}
          </Title>
        </div>
        <div>
          <Title
            level={2}
            className="text-center"
            onClick={() => (job === 'x12' ? jobProcess('billing') : undefined)}
          >
            ค้นหาเอกสารวางบิล
          </Title>
        </div>
      </Flex>
      <Card>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...criteria,
            docDate: [dayjs(criteria.dateFrom), dayjs(criteria.dateTo)],
          }}
          onFinish={onFinish}
          autoComplete="off"
          labelAlign={'left'}
        >
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item name="docNo" label="เลขที่เอกสาร">
                <Input placeholder="เลขที่เอกสาร" size="large" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name={'docDate'} label="วันที่เอกสาร">
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  locale={locale}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<ISellerSearchBillingReq>
                name="customer"
                label="ลูกค้า"
              >
                <CustomerSelector
                  handleSelectData={function (customer: ICustomer): void {
                    form.setFieldsValue({
                      customer: customer?.CMAD_ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<ISellerSearchBillingReq>
                name="docStatus"
                label="สถานะ"
              >
                <Select
                  allowClear
                  options={getSellerBillingStatusOptions()}
                  placeholder="เลือกสถานะ"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap={24} justify="center">
            <button
              disabled={!selectedRowKey.length || mutations.isPending}
              type="button"
              className="btn btn-success"
              style={{ minWidth: 150 }}
              onClick={() => onEntry()}
            >
              {mutations.isPending || mutationGroups.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ส่งเอกสาร'
              )}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ minWidth: 150 }}
            >
              ค้นหา
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={{ minWidth: 150 }}
              onClick={onClear}
            >
              ยกเลิก
            </button>
          </Flex>
        </Form>
      </Card>
      <Card className="mt-3">
        <Table<ISellerBilling>
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          columns={columns}
          dataSource={billings}
          size="small"
          bordered
          rowKey="AR__CHR01"
          pagination={false}
          // pagination={{
          //   total: billings?.length || 0,
          //   showTotal: (total) => `${total} รายการ`,
          //   showSizeChanger: true,
          //   locale: { items_per_page: '/หน้า' },
          // }}
        />
      </Card>
      <SellerInvoiceModal
        open={invoiceModal?.open || false}
        invoiceList={invoiceModal.invoiceList || []}
        onClose={function (): void {
          setInvoiceModal({
            invoiceList: [],
            open: false,
          });
        }}
      />
    </>
  );
};

export default BillingPage;
